// src/lib/helpers/questionnaireForm.ts
//
// Blocks in, form config out. The questionnaire is a config producer, not a
// second renderer — everything below feeds the existing MpfForm machinery.

import type {
  MpfFormBlockConfig,
  MpfFormFieldConfig,
  FormFieldValue,
} from '../components/MpfForm/MpfForm.props';
import type { MpfContentNode } from '../components/MpfContentRenderer/MpfContentRenderer.props';
import { bloodPressureParts } from '../components/MpfInputBloodPressure/MpfInputBloodPressure.props';
import type {
  Questionnaire,
  QuestionnaireAnswers,
  QuestionnaireBlock,
  QuestionnaireQuestionBlock,
} from '../../types/questionnaire';
import { isQuestionnaireQuestionBlock } from '../../types/questionnaire';

/**
 * The template vocabulary is a closed set. V1's form factory throws on an
 * unknown question node and the mapper has to fail as loudly, not skip silently.
 */
export class UnknownQuestionnaireNodeError extends Error {
  constructor(public readonly nodeType: string) {
    super(`Unknown questionnaire node type: ${nodeType}`);
    this.name = 'UnknownQuestionnaireNodeError';
  }
}

const URL_PATTERN = /(https?:\/\/[^\s<]+[^\s<.,:;"')\]])/g;

/**
 * V1 ran a URL-to-anchor regex over info banner text. Emitting `tag: 'a'` nodes
 * instead keeps the text escaped by the renderer rather than injected as HTML.
 */
export function linkifyToContentNodes(text: string): MpfContentNode[] {
  const nodes: MpfContentNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const url = match[0];
    const start = match.index ?? 0;

    if (start > lastIndex) {
      nodes.push({ text: text.slice(lastIndex, start) });
    }

    nodes.push({
      tag: 'a',
      text: url,
      attrs: { href: url, target: '_blank', rel: 'noopener noreferrer' },
    });

    lastIndex = start + url.length;
  }

  if (lastIndex < text.length) {
    nodes.push({ text: text.slice(lastIndex) });
  }

  return [{ tag: 'p', children: nodes }];
}

function optionItems(block: QuestionnaireQuestionBlock) {
  return (block.options ?? []).map((option) => ({
    label: option.label,
    value: option.value,
  }));
}

/** Every question reads as one heading level, whatever widget renders it. */
const QUESTION_HEADING_SIZE = 's' as const;

function baseField(block: QuestionnaireQuestionBlock) {
  return {
    name: block.name,
    label: block.label,
    required: block.required,
    ...(block.hint ? { hint: block.hint } : {}),
  };
}

/**
 * `textInput` and `textarea` share one handler on the backend, both building a
 * TextareaType — so single-line vs multi-line comes from the node name, not the type.
 */
export function mapQuestionnaireBlock(block: QuestionnaireBlock): MpfFormBlockConfig {
  if (block.type === 'description') {
    return { type: 'description', content: linkifyToContentNodes(block.value) };
  }

  if (block.type === 'banner') {
    return {
      type: 'infoBanner',
      heading: block.title,
      content: linkifyToContentNodes(block.text),
    };
  }

  if (!isQuestionnaireQuestionBlock(block)) {
    throw new UnknownQuestionnaireNodeError((block as { type: string }).type);
  }

  const field = baseField(block);

  switch (block.type) {
    case 'text':
      return { ...field, type: 'text', headingLevelSize: QUESTION_HEADING_SIZE };
    case 'textarea':
      return { ...field, type: 'textarea', rows: 5, headingLevelSize: QUESTION_HEADING_SIZE };
    // NHS DS has no number component — a text input with inputmode="numeric".
    case 'quantity':
      return { ...field, type: 'number', headingLevelSize: QUESTION_HEADING_SIZE };
    case 'date':
      return { ...field, type: 'date', legendHeadingSize: QUESTION_HEADING_SIZE };
    case 'bloodPressure':
      return { ...field, type: 'bloodPressure', legendHeadingSize: QUESTION_HEADING_SIZE };
    case 'radios':
      return {
        ...field,
        type: 'radios',
        modelValue: null,
        legendHeadingSize: QUESTION_HEADING_SIZE,
        items: optionItems(block).map((item) => ({ ...item, type: 'radio' as const })),
      };
    case 'checkboxes':
      return {
        ...field,
        type: 'checkboxes',
        modelValue: null,
        legendHeadingSize: QUESTION_HEADING_SIZE,
        items: optionItems(block).map((item) => ({ ...item, type: 'checkbox' as const })),
      };
    case 'select':
      return {
        ...field,
        type: 'select',
        modelValue: null,
        // Clinical safety: an empty first option stops a select answering itself
        // with whichever option happens to sort first.
        includeEmptyOption: true,
        headingLevelSize: QUESTION_HEADING_SIZE,
        items: optionItems(block),
      };
    default:
      throw new UnknownQuestionnaireNodeError((block as { type: string }).type);
  }
}

export function mapQuestionnaireBlocks(blocks: QuestionnaireBlock[]): MpfFormBlockConfig[] {
  return blocks.map(mapQuestionnaireBlock);
}

export function questionNames(questionnaire: Questionnaire): string[] {
  return questionBlocks(questionnaire).map((block) => block.name);
}

function questionBlocks(questionnaire: Questionnaire): QuestionnaireQuestionBlock[] {
  return questionnaire.questionnaire.filter(isQuestionnaireQuestionBlock);
}

/**
 * MpfForm answers with its own field values; the payload is keyed by question
 * name, and blood pressure splits into the two values the filing handler reads.
 */
export function toSubmittedAnswers(
  questionnaire: Questionnaire,
  values: Record<string, FormFieldValue>,
): QuestionnaireAnswers {
  const answers: QuestionnaireAnswers = {};

  for (const block of questionBlocks(questionnaire)) {
    const value = values[block.name];

    // An unanswered question is left out entirely. The endpoint skips a name it
    // has no key for, but validates one sent as null against the option list.
    if (block.type === 'bloodPressure') {
      const parts = bloodPressureParts(typeof value === 'string' ? value : '');
      if (parts.systolic || parts.diastolic) {
        answers[block.name] = parts;
      }
      continue;
    }

    if (block.type === 'checkboxes') {
      const chosen = Array.isArray(value)
        ? value.filter((entry) => entry != null && entry !== '')
        : [];
      if (chosen.length) {
        answers[block.name] = chosen.map((entry) => String(entry));
      }
      continue;
    }

    if (value == null || String(value).trim() === '') {
      continue;
    }

    answers[block.name] = String(value);
  }

  return answers;
}

/** Field values MpfForm can start from, so a section keeps its answers on back. */
export function toFieldValues(
  fields: MpfFormBlockConfig[],
  answers: QuestionnaireAnswers,
): MpfFormBlockConfig[] {
  return fields.map((field) => {
    if (!('name' in field)) return field;

    const answer = answers[(field as MpfFormFieldConfig).name];
    if (answer == null) return field;

    if (field.type === 'bloodPressure') {
      const { systolic, diastolic } = answer as { systolic: string; diastolic: string };
      return systolic || diastolic ? { ...field, initialValue: `${systolic}/${diastolic}` } : field;
    }

    return { ...field, initialValue: answer as FormFieldValue };
  });
}
